import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import CloseIcon from '@/shared/assets/close-icon.svg?react';

import { cn } from '@/shared/utils/styling';
import { useVisualViewport } from '@/shared/lib';

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-70 bg-black/50',
        className
      )}
      {...props}
    />
  );
}

function renderCloseButton({
  showCloseButton,
  onClose,
  customCloseButton,
}: {
  showCloseButton: boolean;
  onClose?: () => void;
  customCloseButton?: React.ReactNode;
}) {
  if (!showCloseButton) return null;

  if (customCloseButton) {
    return customCloseButton;
  }

  const buttonClassName =
    'flex justify-center items-center w-[4.2rem] h-[4.2rem] bg-background cursor-pointer border border-border absolute sm:top-[3.2rem] top-[2.4rem] sm:right-[3.2rem] right-[2.4rem] rounded-full hover:bg-muted transition-colors focus:outline-hidden disabled:pointer-events-none z-10';
  const ButtonContent = (
    <>
      <CloseIcon className="size-9 w-[2.4rem] h-[2.4rem] text-foreground" />
      <span className="sr-only">Close</span>
    </>
  );

  if (onClose) {
    return (
      <button onClick={onClose} className={buttonClassName}>
        {ButtonContent}
      </button>
    );
  }

  return (
    <DialogPrimitive.Close
      data-slot="dialog-close"
      className={cn(
        buttonClassName,
        "[&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-9"
      )}>
      {ButtonContent}
    </DialogPrimitive.Close>
  );
}

function DialogContent({
  className,
  children,
  showCloseButton = true,
  customCloseButton,
  onClose,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean;
  customCloseButton?: React.ReactNode;
  onClose?: () => void;
}) {
  const closeButton = renderCloseButton({
    showCloseButton,
    onClose,
    customCloseButton,
  });

  const { height, offsetTop, isKeyboardVisible } = useVisualViewport();

  const dynamicStyles = React.useMemo(() => {
    if (isKeyboardVisible) {
      const centerY = offsetTop + height / 2;
      const maxHeight = Math.min(window.innerHeight * 0.9, height * 0.9);

      return {
        top: `${centerY}px`,
        maxHeight: `${maxHeight}px`,
        transition: 'all 0.3s ease-in-out',
      };
    }

    return {
      transition: 'all 0.3s ease-in-out',
    };
  }, [height, offsetTop, isKeyboardVisible]);

  return (
    <DialogPortal data-slot="dialog-portal">
      <DialogOverlay className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0 data-[state=open]:duration-300 data-[state=closed]:duration-400" />
      <DialogPrimitive.Content
        data-slot="dialog-content"
        className={cn(
          'bg-background fixed top-[50%] left-[50%] z-70 flex flex-col w-full max-w-[calc(100%-2rem)] translate-x-[-50%] translate-y-[-50%] gap-6 rounded-lg border shadow-lg sm:max-w-3xl',
          'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:slide-in-from-bottom data-[state=closed]:slide-out-to-bottom-[100vh] data-[state=open]:duration-340 data-[state=closed]:duration-400',
          className
        )}
        style={dynamicStyles}
        {...props}>
        {children}
        {closeButton}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2 text-center sm:text-left', className)}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('text-xl leading-none font-semibold', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn('text-muted-foreground text-base', className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
